import React, { useState, useRef, forwardRef } from 'react';
import { useMergeRefs } from '@floating-ui/react';
import classNames from 'classnames';
import styles from './FilePicker.module.scss';
import { Button, Text, Icon, IconList } from 'components/core';
import { Stack } from 'components/layout';

export type FilePickerProps = {
  required?: boolean;
  id?: string;
  acceptedFileTypes: string[];
  acceptedMimeTypes?: string[];
  promptText: string;
  subText?: string;
  buttonText: string;
  multiple?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  errorMessage?: string | null;
  maxFileSizeMB?: number;
  invalidFileTypeMessage?: string;
  'aria-describedby'?: string;
  errorMessageId?: string;
  onChange: (files: File[]) => void;
  onBlur?: () => void;
};

const validateFile = (
  file: File,
  acceptedFileTypes: string[],
  acceptedMimeTypes: string[] | undefined,
  maxFileSizeMB: number | undefined,
  invalidFileTypeMessage?: string
): { valid: boolean; error?: string } => {
  const fileName = file.name.toLowerCase();
  const hasValidExtension = acceptedFileTypes.some((ext) =>
    fileName.endsWith(ext.toLowerCase())
  );

  if (!hasValidExtension) {
    return {
      valid: false,
      error:
        invalidFileTypeMessage ||
        `This file is not an accepted format. You can upload a file with ${acceptedFileTypes.join(
          ', '
        )} extension.`,
    };
  }

  if (acceptedMimeTypes && acceptedMimeTypes.length > 0) {
    const hasValidMimeType = acceptedMimeTypes.includes(file.type);
    if (!hasValidMimeType) {
      return {
        valid: false,
        error:
          invalidFileTypeMessage ||
          `This file is not an accepted format. You can upload a file with ${acceptedFileTypes.join(
            ', '
          )} extension.`,
      };
    }
  }

  if (maxFileSizeMB) {
    const maxBytes = maxFileSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      return {
        valid: false,
        error: `This file is too big. You can upload files up to ${maxFileSizeMB}MB.`,
      };
    }
  }

  return { valid: true };
};

const FilePicker = forwardRef<HTMLInputElement, FilePickerProps>(
  (
    {
      required = false,
      acceptedFileTypes,
      acceptedMimeTypes,
      promptText,
      subText,
      buttonText,
      multiple = false,
      disabled = false,
      hasError = false,
      errorMessage,
      maxFileSizeMB,
      invalidFileTypeMessage,
      id,
      'aria-describedby': ariaDescribedby,
      errorMessageId = 'file-picker-error',
      onChange,
      onBlur,
    },
    forwardedRef
  ) => {
    const [isDragged, setDragged] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const internalFileRef = useRef<HTMLInputElement>(null);

    // Merge forwarded ref (from RHF) with internal ref (for component logic)
    const fileRef = useMergeRefs([forwardedRef, internalFileRef]);

    const validateFiles = (
      files: File[]
    ): { validatedFiles: File[]; firstError: string | null } => {
      const validatedFiles: File[] = [];
      let firstError: string | null = null;

      for (const file of files) {
        const validation = validateFile(
          file,
          acceptedFileTypes,
          acceptedMimeTypes,
          maxFileSizeMB,
          invalidFileTypeMessage
        );

        if (validation.valid) {
          validatedFiles.push(file);
        } else if (!firstError) {
          firstError = validation.error || 'Invalid file';
        }
      }

      return { validatedFiles, firstError };
    };

    const handleValidatedFiles = (
      validatedFiles: File[],
      firstError: string | null
    ) => {
      if (validatedFiles.length > 0) {
        setValidationError(null);
        onChange(validatedFiles);
        if (internalFileRef.current) {
          internalFileRef.current.value = '';
        }
      } else if (firstError) {
        setValidationError(firstError);
        // Don't call onChange on validation failure
        // Focus the input so screen readers announce the error
        if (internalFileRef.current) {
          internalFileRef.current.focus();
        }
      }
    };

    const handleFiles = (files: FileList | null) => {
      if (!files || files.length === 0) {
        return;
      }

      const fileArray = Array.from(files);
      const { validatedFiles, firstError } = validateFiles(fileArray);
      handleValidatedFiles(validatedFiles, firstError);
    };

    const onFileChange = () => {
      handleFiles(internalFileRef.current?.files || null);
    };

    const acceptAttribute = acceptedFileTypes.join(',');

    return (
      <div>
        <div
          className={classNames(styles.dropZone, {
            [styles.dragged]: isDragged,
            [styles.error]: hasError || validationError || errorMessage,
            [styles.disabled]: disabled,
          })}
          onDragEnter={() => !disabled && setDragged(true)}
        >
          <label className={styles.label}>
            <input
              required={required}
              ref={fileRef}
              type="file"
              id={id}
              accept={acceptAttribute}
              multiple={multiple}
              disabled={disabled}
              className={styles.input}
              onDragLeave={() => setDragged(false)}
              onDrop={() => setDragged(false)}
              onChange={onFileChange}
              onBlur={onBlur}
              aria-invalid={hasError || !!validationError || !!errorMessage}
              aria-errormessage={
                hasError || validationError || errorMessage
                  ? errorMessageId
                  : undefined
              }
              aria-describedby={`file-picker-prompt ${ariaDescribedby || ''} ${
                errorMessageId || ''
              }`}
            />
            <Stack spacing={3} alignX="center">
              <Icon
                icon={
                  isDragged
                    ? IconList.solid.faCloudUploadAlt
                    : IconList.light.faCloudUpload
                }
                variant={isDragged ? 'primary' : 'neutral'}
                className={styles.icon}
              />
              <Text id="file-picker-prompt" size="sm" component="span">
                {promptText}
              </Text>
              {subText && (
                <Text size="sm" variant="weak" component="span">
                  {subText}
                </Text>
              )}
              <Button
                component="span"
                variant="outline"
                size="sm"
                className={styles.fakeButton}
              >
                {buttonText}
              </Button>
            </Stack>
          </label>
        </div>
        <div aria-live="assertive">
          {(validationError || errorMessage) && (
            <div className={styles.errorMessage}>
              <Text size="xs" variant="danger" id={errorMessageId}>
                {errorMessage || validationError}
              </Text>
            </div>
          )}
        </div>
      </div>
    );
  }
);

FilePicker.displayName = 'FilePicker';

export default FilePicker;
